import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Grid from '../components/Grid'; // Assuming Grid is a reusable component for displaying data
import { FaEye } from "react-icons/fa"; 
import { DataGrid } from '@mui/x-data-grid';
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CreditNote({ onAddCreditNoteClick }) {
  const [creditNotes, setCreditNotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);

  useEffect(() => {
    const fetchCreditNotes = async () => {
      try {
        const token = sessionStorage.getItem('token');
        if (!token) {
          toast.error('Authentication token is missing. Please log in again.');
          return;
        }
  
        const response = await axios.get(`${API_BASE_URL}/creditNotes/get`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        // Sort by createdAt before setting state
        const sortedCreditNotes = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  
        setCreditNotes(sortedCreditNotes);
  
      } catch (error) {
        console.error('Error fetching credit notes:', error);
        toast.error('Error fetching data');
      }
    };
  
    fetchCreditNotes();
  }, []);
  


  const handleDownloadExcel = async () => {
    const dataToExport =
      searchTerm.trim().length > 0 ? filteredCreditNotes : creditNotes;

    if (dataToExport.length === 0) {
      toast.warn("No credit note data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Credit Notes");

    const headerRow = [
      "Credit Note Code",
      "Customer Code",
      "Customer Name",
      "Issue Date",
      "Credited Amount (LKR)",
      "Balance Amount (LKR)",
      "Status",
      "Created By"
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    dataToExport.forEach((item) => {
      worksheet.addRow([
        item.credit_note_code,
        item.customer_id?.customer_code || "N/A",
        item.customer_id?.customer_name || "N/A",
        item.issue_date || "N/A",
        Number(item.total_credit_amount || 0).toFixed(2),
        Number(item.balance || 0).toFixed(2),
        item.status,
        item.createdBy?.username || "Unknown"
      ]);
    });

    worksheet.columns.forEach((column) => {
      column.width = column.header
        ? column.header.toString().length < 20
          ? 20
          : column.header.toString().length
        : 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "credit_notes.xlsx");
  };


  const fetchCreditNoteDetails = async (_id) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token is missing. Please log in again.');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/creditNotes/get/${_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });


      setSelectedCreditNote(response.data); 

    } catch (error) {
      console.error('Error fetching credit note details:', error);
      toast.error('Failed to fetch credit note details');
    }
  };

  const [sort] = useState({
    field: 'date',
    direction: 'asc',
  });
  const [selectedSearchField, setSelectedSearchField] = useState('_id');

  const getValueByPath = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  const filteredCreditNotes = creditNotes
  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // latest first
  .filter((note) => {
    if (searchTerm.trim() === '') return true; // No filter if empty search

    const fieldValue = getValueByPath(note, selectedSearchField);

    if (fieldValue === undefined || fieldValue === null) return false;

    return fieldValue.toString().toLowerCase().includes(searchTerm.toLowerCase());
  });


    const columns = [
      { field: 'credit_note_code', headerName: 'Credit Note Code', flex: 1 },
      { field: 'issue_date', headerName: 'Issue Date', flex: 1,
        renderCell: (params) => (
          <span>
            {params.value ? new Date(params.value).toLocaleDateString("en-GB") : "N/A"}
          </span>
        ),
      },
      { 
        field: 'customer_code', 
        headerName: 'Customer Code', 
        flex: 1,
        renderCell: (params) => {
          // Accessing customer_code directly from the customer_id object
          return <span>{params.row.customer_id?.customer_code || "No Customer Code"}</span>
        }
      },
      { 
        field: 'customer_name', 
        headerName: 'Customer Name', 
        flex: 1,
        renderCell: (params) => {
          // Accessing customer_name directly from the customer_id object
          return <span>{params.row.customer_id?.customer_name || "No Customer Name"}</span>
        }
      },
      { field: 'total_credit_amount', headerName: 'Credited Amount (LKR)', flex: 1, headerAlign: "right", align: "right", renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` },
      { field: 'balance', headerName: 'Balance Amount (LKR)', flex: 1, headerAlign: "right", align: "right", renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` },
      { 
        field: "status",
        headerName: "Status",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
            const status = params.row.status;
        
            // Map statuses to classes
            const statusClassMap = {
              "Unsettled": "bg-gray-600 text-white",
              "Partially Settled": "bg-[#8BC34A] text-white",
              "Fully Settled": "bg-[#90A4AE] text-white",
            };
      
          // Get the class based on status or default to a neutral color
          const statusClass = statusClassMap[status] || "bg-gray-500 text-white"; // Default class if status not found
      
          return (
            <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
              {status}
            </span>
          );
        }, 
      },
      {
        field: "createdBy",
        headerName: "Created By",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return (
            <span>
              {params.row.createdBy?.username || "Unknown"}
            </span>
          );
        }
      },
      { 
        field: "action",
        headerName: "Action",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <div className="flex justify-center items-center p-3 space-x-3">
            <button
              onClick={() => fetchCreditNoteDetails(params.row._id)}
              className="bg-[#FBC02D] text-white p-2 flex items-center justify-center"
            >
              <FaEye className="text-white" />
            </button>
          </div>
        ),
      },
    ];
    

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Credit Notes</h1>
          <button
            onClick={onAddCreditNoteClick}
            className="bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Add Credit Note
          </button>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
                <select
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                  value={selectedSearchField}
                  onChange={(e) => setSelectedSearchField(e.target.value)}
                >
                  <option value="credit_note_code">Credit Note Code</option>
                  <option value="customer_id.customer_code">Customer Code</option>
                  <option value="customer_id.customer_name">Customer Name</option>
                  <option value="issue_date">Issue Date</option>
                  <option value="total_credit_amount">Amount</option>
                  <option value="balance">Balance</option>
                  <option value="status">Status</option>
                  <option value="createdBy.username">Created By</option>
                </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search credit notes"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>
          <Grid rows={filteredCreditNotes} columns={columns} getRowId={(row) => row._id}/>
          {selectedCreditNote && (
            <div
              className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
              style={{ overflowY: "auto" }}
            >
              <div
                className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto"
                style={{ maxHeight: "80vh", overflowY: "auto",  }}
              >
                <button
                  onClick={() => setSelectedCreditNote(null)}
                  className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-lg hover:bg-red-700"
                >
                  &times;
                </button>
                <h2 className="text-3xl font-bold mb-4">Credit Note Details</h2>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Credit Note ID</label>
                  <input
                    type="text"
                    value={selectedCreditNote.credit_note_code || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Customer Code</label>
                  <input
                    type="text"
                    value={selectedCreditNote.customer_id.customer_code || "Unkown"}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Customer Name</label>
                  <input
                    type="text"
                    value={selectedCreditNote.customer_id.customer_name || "Unkown"}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Credited Amount</label>
                  <input
                    type="text"
                    value={selectedCreditNote.total_credit_amount || 0}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Balance Amount</label>
                  <input
                    type="text"
                    value={selectedCreditNote.balance || 0}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Date</label>
                  <input
                    type="text"
                    value={selectedCreditNote.issue_date || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Reason</label>
                  <textarea
                    value={selectedCreditNote.reason || ""}
                    readOnly
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none"
                  />
                </div>
                {/* Display payment receipts */}
                <h3 className="text-lg font-semibold mt-4 mb-2">Invoices</h3>
                <div  style={{ height: 400, width: "100%" }}>
                  <DataGrid
                  rows={selectedCreditNote.applied_invoices.map((invoice, index) => ({
                    id: index + 1,
                    invoiceID: invoice.invoice_id?.invoice_id,
                    credit_amount: invoice.credit_amount,
                  }))}
                  columns={[
                    { field: "id", headerName: "Number", width: 80, flex: 0.3 },
                    { field: "invoiceID", headerName: "Invoice  ID", width: 150, flex: 1 },
                    { field: "credit_amount", headerName: "Applied Amount (LKR)", width: 140, flex: 1, headerAlign: "right", align: "right", renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` },
                  ]}
                  pageSize={16}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreditNote;
